import { decodeJwtPayload } from "@/lib/authToken";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const IMAGE_BASE_URL = (import.meta.env.VITE_IMAGE_BASE_URL || API_BASE_URL).replace(/\/$/, "");
const OTP_SEND_URL = `${API_BASE_URL}/api/verification/send/otp`;
const OTP_VERIFY_URL = `${API_BASE_URL}/api/verification/verify/otp`;
const PUBLIC_LISTINGS_URL = "https://omnivaas.com/api/public/properties/listings";
const PUBLIC_PROPERTY_DETAIL_URL = "https://omnivaas.com/api/public/properties";
const PUBLIC_RECENT_LISTINGS_URL = "https://omnivaas.com/api/public/properties/recent-listings";
const PUBLIC_SOCIETIES_URL = "https://omnivaas.com/api/public/societies";
const PUBLIC_CONTACT_US_URL = "/api/public/contact-us";
const PUBLIC_SCHEDULE_VISITS_URL = "/api/public/schedule-visits";
const PUBLIC_LISTING_LEADS_URL = "/api/public/listing-leads";
const CONTACT_US_LIST_URL = "/api/contact-us/list";
const CONTACT_US_DETAIL_URL = "/api/contact-us";
const SCHEDULE_VISITS_LIST_URL = "/api/schedule-visits/list";
const SCHEDULE_VISITS_DETAIL_URL = "/api/schedule-visits";
const LISTING_LEADS_LIST_URL = "/api/listing-leads/list";
const LISTING_LEADS_DETAIL_URL = "/api/listing-leads";
const PROPERTY_ADMIN_LIST_URL = "/api/property/details/admin/properties/list";
const PROPERTY_UPLOAD_URL = `${API_BASE_URL}/api/property/details/upload`;
const PROPERTY_DETAIL_URL = `${API_BASE_URL}/api/property/details/by/id`;
const PROPERTY_EDIT_URL = `${API_BASE_URL}/api/property/details`;
const BOOKED_UNIT_URL = `${API_BASE_URL}/api/tenant/save`;
const BOOKED_UNIT_DETAIL_URL = `${API_BASE_URL}/api/tenant/details`;
const SOCIETY_MASTER_URL = `${API_BASE_URL}/api/society/master`;
const SOCIETY_ADD_URL = `${API_BASE_URL}/api/society/master/save`;
const SOCIETY_SUMMARY_LIST_URL = `${API_BASE_URL}/api/society/master/summary/list`;
const USER_ADD_URL = `${API_BASE_URL}/api/users/add`;
const USER_LIST_URL = `${API_BASE_URL}/api/users`;
const USER_BY_EMAIL_URL = `${API_BASE_URL}/api/users/get/user/by/email`;
const AUTH_TOKEN_KEY = "omnivaas.adminToken";
const AUTH_TOKEN_EXP_KEY = "omnivaas.adminTokenExp";
const OTP_PATHS = ["/api/verification/send/otp", "/api/verification/verify/otp"];

function isOtpRequest(path) {
  return OTP_PATHS.some((otpPath) => String(path).includes(otpPath));
}

function getSessionToken() {
  if (typeof window === "undefined") {
    return "";
  }

  const token = sessionStorage.getItem(AUTH_TOKEN_KEY);
  const exp = Number(sessionStorage.getItem(AUTH_TOKEN_EXP_KEY) || 0);

  if (!token || !exp || exp * 1000 <= Date.now()) {
    sessionStorage.removeItem("omnivaas.adminAuth");
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem(AUTH_TOKEN_EXP_KEY);
    sessionStorage.removeItem("omnivaas.adminTokenIat");
    return "";
  }

  return token;
}

function getListingToken() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const raw = localStorage.getItem("omnivaas.listingAuth");
    const parsed = raw ? JSON.parse(raw) : null;
    const token = parsed?.currentUser?.token || "";
    const exp = parsed?.currentUser?.exp || decodeJwtPayload(token)?.exp;

    if (!token || (exp && Number(exp) * 1000 <= Date.now())) {
      localStorage.removeItem("omnivaas.listingAuth");
      localStorage.removeItem("omnivaas.listingOtpId");
      localStorage.removeItem("omnivaas.listingPendingEmail");
      localStorage.removeItem("omnivaas.listingPendingMobile");
      return "";
    }

    return token;
  } catch {
    localStorage.removeItem("omnivaas.listingAuth");
    return "";
  }
}

function getAuthHeaders(path) {
  const token = isOtpRequest(path) ? "" : getSessionToken() || getListingToken();

  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function buildUrl(path, query = {}) {
  if (/^https?:\/\//i.test(path)) {
    const absoluteUrl = new URL(path);

    Object.entries(query).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) =>
          absoluteUrl.searchParams.append(key, String(item)),
        );
        return;
      }

      absoluteUrl.searchParams.set(key, String(value));
    });

    return absoluteUrl.toString();
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(
    `${API_BASE_URL}${normalizedPath}`,
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  );

  Object.entries(query).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, String(item)));
      return;
    }

    url.searchParams.set(key, String(value));
  });

  if (API_BASE_URL) {
    return url.toString();
  }

  return `${url.pathname}${url.search}`;
}

async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text ? { message: text } : null;
}

async function apiRequest(path, options = {}) {
  const { query, body, headers = {}, auth = true, ...rest } = options;
  const isFormData = body instanceof FormData;
  const requestHeaders = {
    ...(auth ? getAuthHeaders(path) : {}),
    ...headers,
  };

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: isFormData
      ? requestHeaders
      : {
          "Content-Type": "application/json",
          ...requestHeaders,
        },
    body:
      body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });

  const data = await parseResponse(response);

  // Check for HTTP error status
  if (!response.ok) {
    const errorMsg = data?.message || "Request failed";
    const error = new Error(errorMsg);
    error.statusCode = response.status;
    throw error;
  }

  // Also check if response indicates an error even with 200 status
  if (data?.statusCode && data.statusCode >= 400) {
    const errorMsg = data?.message || "Request failed";
    const error = new Error(errorMsg);
    error.statusCode = data.statusCode;
    throw error;
  }

  return data;
}

function normalizePropertyRecord(property) {
  if (!property || typeof property !== "object") {
    return property;
  }

  const flatType = property.flatType || property.property_type;
  const monthlyRent = property.monthlyRent ?? property.price;
  const societyName = property.societyName || property.title;
  const propertyId = property.propertyId ?? property.id;
  const bedrooms = String(flatType || "").match(/\d+/)?.[0];
  const availability = property.availability || property.availability_type || "No";
  const towerUnit = property.towerNo ? `T ${property.towerNo}` : "";
  const unit = [towerUnit, property.flatNo].filter(Boolean).join(" - ");
  const title = societyName || property.propertyType || "Property";
  const propertyType = property.propertyType || property.property_type || flatType || "";
  const imageSource =
    property.images ||
    property.propertyImageUrls ||
    property.propertyImages ||
    property.imageUrls ||
    property.imageUrl ||
    property.propertyImageUrl ||
    property.thumbnailUrl ||
    [];
  const images = Array.isArray(imageSource)
    ? imageSource
    : imageSource
      ? [imageSource]
      : [];
  const area = property.area || property.size || property.carpetArea;
  const facing =
    property.facing ||
    property.facingDirection ||
    property.propertyFacing ||
    property.direction;
  const floor =
    property.floor ||
    property.floorNo ||
    property.floorNumber ||
    property.propertyFloor;
  const otherRoom =
    property.otherRoom ||
    property.otherRooms ||
    property.other_room ||
    property.otherRoomDetails ||
    property.other_room_details ||
    property.additionalRoom ||
    property.extraRoom;
  const furnishingDetails =
    property.furnishingDetails ||
    property.furnishing_details ||
    property.furnishingDescription;
  const noOfBedroom =
    property.noOfBedroom ??
    property.noOfBedrooms ??
    property.numberOfBedrooms ??
    property.bedrooms;
  const noOfBathroom =
    property.noOfBathroom ??
    property.noOfBathrooms ??
    property.numberOfBathrooms ??
    property.bathrooms;
  const noOfBalconies =
    property.noOfBalconies ??
    property.noOfBalcony ??
    property.numberOfBalconies ??
    property.balconies;
  const willingToRentOutTo =
    property.willingToRentOutTo ||
    property.willing_to_rent_out_to ||
    property.rentOutTo ||
    property.preferredTenant;

  return {
    ...property,
    id: propertyId,
    title,
    price: monthlyRent,
    images,
    area,
    Society: title,
    Unit: unit,
    Rent: monthlyRent,
    listing_type: property.listing_type || property.listingType || "rent",
    property_type: propertyType,
    location: property.location || title || "",
    description:
      property.description ||
      [flatType?.replace(/_/g, " "), property.furnishingStatus?.replace(/_/g, " "), property.status]
        .filter(Boolean)
        .join(" • "),
    bedrooms: property.bedrooms || noOfBedroom || bedrooms,
    bathrooms: property.bathrooms || noOfBathroom,
    balconies: property.balconies || noOfBalconies,
    deposit: property.deposit,
    facing,
    floor,
    otherRoom,
    furnishingDetails,
    noOfBedroom,
    noOfBathroom,
    noOfBalconies,
    willingToRentOutTo,
    availability
  };
}

function normalizePropertyList(items) {
  return items.map(normalizePropertyRecord);
}

function normalizeListResponse(data) {
  if (Array.isArray(data)) {
    return normalizePropertyList(data);
  }

  if (Array.isArray(data?.items)) {
    return normalizePropertyList(data.items);
  }

  if (Array.isArray(data?.data)) {
    return normalizePropertyList(data.data);
  }

  if (Array.isArray(data?.content)) {
    return normalizePropertyList(data.content);
  }

  if (Array.isArray(data?.data?.content)) {
    return normalizePropertyList(data.data.content);
  }

  return [];
}

function normalizePaginatedPropertyResponse(data) {
  const source = data?.data && !Array.isArray(data.data) ? data.data : data;
  const content = Array.isArray(source?.content)
    ? source.content
    : Array.isArray(source)
      ? source
      : [];

  return {
    items: normalizePropertyList(content),
    page: Number(source?.page || 0),
    size: Number(source?.size || content.length || 0),
    totalElements: Number(source?.totalElements ?? content.length),
    totalPages: Number(source?.totalPages || 1),
    last: Boolean(source?.last ?? true),
  };
}

function unwrapResponseData(data) {
  if (data?.data !== undefined) {
    return data.data;
  }

  return data;
}

function normalizePaginatedResponse(data) {
  const source = data?.data && !Array.isArray(data.data) ? data.data : data;
  const content = Array.isArray(source?.content)
    ? source.content
    : Array.isArray(source?.items)
      ? source.items
      : Array.isArray(source)
        ? source
        : [];

  return {
    items: content,
    page: Number(source?.page ?? source?.number ?? 0),
    size: Number(source?.size || content.length || 0),
    totalElements: Number(source?.totalElements ?? source?.totalItems ?? source?.total ?? content.length),
    totalPages: Number(source?.totalPages || 1),
    last: Boolean(source?.last ?? true),
  };
}

function normalizeUserListResponse(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.content)) {
    return data.content;
  }

  if (Array.isArray(data?.data?.content)) {
    return data.data.content;
  }

  return [];
}

function normalizeSocietyListResponse(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.content)) {
    return data.content;
  }

  if (Array.isArray(data?.data?.content)) {
    return data.data.content;
  }

  if (Array.isArray(data?.data?.items)) {
    return data.data.items;
  }

  return [];
}

function normalizePublicSocietyRecords(data) {
  return normalizeSocietyListResponse(data).filter(
    (record) => record && typeof record === "object",
  );
}

function getUniquePublicSocietyValues(records, field) {
  const uniqueValues = new Map();

  records.forEach((record) => {
    const value = String(record?.[field] || "").trim();

    if (value && !uniqueValues.has(value.toLowerCase())) {
      uniqueValues.set(value.toLowerCase(), value);
    }
  });

  return Array.from(uniqueValues.values()).sort((a, b) => a.localeCompare(b));
}

function sortByNewest(items) {
  return [...items].sort((a, b) => {
    const aTime = new Date(a.createdAt || a.created || 0).getTime();
    const bTime = new Date(b.createdAt || b.created || 0).getTime();
    return bTime - aTime;
  });
}

function createCrudApi(basePath) {
  return {
    async getAll({ limit, query } = {}) {
      const data = await apiRequest(basePath, {
        method: "GET",
        query: { limit, ...query },
      });
      return normalizeListResponse(data);
    },
    getById(id) {
      return apiRequest(`${basePath}/${id}`, { method: "GET" });
    },
    create(body) {
      return apiRequest(basePath, { method: "POST", body });
    },
    update(id, body) {
      return apiRequest(`${basePath}/${id}`, { method: "PUT", body });
    },
    remove(id) {
      return apiRequest(`${basePath}/${id}`, { method: "DELETE" });
    },
  };
}

export const propertyApi = {
  ...createCrudApi("/api/properties"),
  remove(id) {
    return apiRequest(
      `${API_BASE_URL}/api/property/details/delete/${id}`,
      {
        method: "DELETE",
      }
    );
  },
  getDetail(id) {
    return apiRequest(`${PROPERTY_DETAIL_URL}/${id}`, {
      method: "GET",
    });
  },
  update(id, propertyDetails, { images = [], possessionLetter = null, replaceImages = false } = {}) {
    const shouldUseMultipart = images.length > 0 || possessionLetter;

    if (shouldUseMultipart) {
      const formData = new FormData();
      formData.append("propertyDetails", JSON.stringify(propertyDetails));

      images.forEach((image) => {
        formData.append("images", image);
      });

      if (possessionLetter) {
        formData.append("possessionLetter", possessionLetter);
      }

      return apiRequest(`${PROPERTY_EDIT_URL}/${id}`, {
        method: "PUT",
        query: {
          replaceImages,
        },
        body: formData,
      });
    }

    return apiRequest(`${PROPERTY_EDIT_URL}/${id}`, {
      method: "PUT",
      body: propertyDetails,
    });
  },
  async getByOwnerId(ownerId) {
    const data = await apiRequest(`${API_BASE_URL}/api/property/details/by-owner-id/${ownerId}`, {
      method: "GET",
    });
    return normalizeListResponse(data);
  },
  async getAll({ limit = 20, query = {} } = {}) {
    const data = await apiRequest(PROPERTY_ADMIN_LIST_URL, {
      method: "GET",
      query: {
        page: 0,
        size: limit,
        sortBy: "lastModifiedDate",
        direction: "asc",
        availability: "Yes",
        ...query,
      },
    });
    return normalizeListResponse(data);
  },
  async getPage({ page = 0, size = 20, query = {} } = {}) {
    const data = await apiRequest(PROPERTY_ADMIN_LIST_URL, {
      method: "GET",
      query: {
        page,
        size,
        sortBy: "lastModifiedDate",
        direction: "asc",
        ...query,
      },
    });
    return normalizePaginatedPropertyResponse(data);
  },
  async getPublicRecent({ page = 0, size = 10 } = {}) {
    const data = await apiRequest(PUBLIC_RECENT_LISTINGS_URL, {
      method: "GET",
      auth: false,
      query: {
        page,
        size,
      },
    });
    return normalizePaginatedPropertyResponse(data);
  },
  async getPublicListings({ page = 0, size = 10, query = {} } = {}) {
    const data = await apiRequest(PUBLIC_LISTINGS_URL, {
      method: "GET",
      auth: false,
      query: {
        page,
        size,
        ...query,
      },
    });
    return normalizePaginatedPropertyResponse(data);
  },
  async getPublicDetail(id) {
    const data = await apiRequest(`${PUBLIC_PROPERTY_DETAIL_URL}/${id}`, {
      method: "GET",
      auth: false,
    });
    return normalizePropertyRecord(data);
  },
  async getPublicSocietyDirectory() {
    const data = await apiRequest(PUBLIC_SOCIETIES_URL, {
      method: "GET",
      auth: false,
    });

    return normalizePublicSocietyRecords(data);
  },
  async getPublicLocations() {
    const records = await this.getPublicSocietyDirectory();
    return getUniquePublicSocietyValues(records, "location");
  },
  async getPublicSocieties() {
    const records = await this.getPublicSocietyDirectory();
    return getUniquePublicSocietyValues(records, "societyName");
  },
  async getFeatured(limit = 4) {
    const properties = await this.getAll({ limit });
    return sortByNewest(properties).slice(0, limit);
  },
};

export const agentApi = createCrudApi("/api/agents");
export const inquiryApi = createCrudApi("/api/inquiries");
export const contactApi = {
  submit(body) {
    return apiRequest(PUBLIC_CONTACT_US_URL, {
      method: "POST",
      auth: false,
      body,
    });
  },
};

export const scheduleVisitApi = {
  submit(body) {
    return apiRequest(PUBLIC_SCHEDULE_VISITS_URL, {
      method: "POST",
      auth: false,
      body,
    });
  },
};

export const listingLeadApi = {
  submit(body) {
    return apiRequest(PUBLIC_LISTING_LEADS_URL, {
      method: "POST",
      auth: false,
      body,
    });
  },
  async getPage({ page = 0, size = 10 } = {}) {
    const data = await apiRequest(LISTING_LEADS_LIST_URL, {
      method: "GET",
      query: {
        page,
        size,
      },
    });

    return normalizePaginatedResponse(data);
  },
  async getById(id) {
    const data = await apiRequest(`${LISTING_LEADS_DETAIL_URL}/${id}`, {
      method: "GET",
    });

    return unwrapResponseData(data);
  },
  updateStatus(id, body) {
    return apiRequest(`${LISTING_LEADS_DETAIL_URL}/${id}/status`, {
      method: "PATCH",
      body,
    });
  },
};

export const contactLeadApi = {
  async getPage({ page = 0, size = 10 } = {}) {
    const data = await apiRequest(CONTACT_US_LIST_URL, {
      method: "GET",
      query: {
        page,
        size,
      },
    });

    return normalizePaginatedResponse(data);
  },
  async getById(id) {
    const data = await apiRequest(`${CONTACT_US_DETAIL_URL}/${id}`, {
      method: "GET",
    });

    return unwrapResponseData(data);
  },
};

export const scheduleVisitLeadApi = {
  async getPage({ page = 0, size = 10 } = {}) {
    const data = await apiRequest(SCHEDULE_VISITS_LIST_URL, {
      method: "GET",
      query: {
        page,
        size,
      },
    });

    return normalizePaginatedResponse(data);
  },
  async getById(id) {
    const data = await apiRequest(`${SCHEDULE_VISITS_DETAIL_URL}/${id}`, {
      method: "GET",
    });

    return unwrapResponseData(data);
  },
  updateStatus(id, body) {
    return apiRequest(`${SCHEDULE_VISITS_DETAIL_URL}/${id}/status`, {
      method: "PATCH",
      body,
    });
  },
};

export const userApi = {
  async getAll() {
    const data = await apiRequest(USER_LIST_URL, {
      method: "GET",
    });
    return normalizeUserListResponse(data);
  },
  getById(id) {
    return apiRequest(`${USER_LIST_URL}/${id}`, {
      method: "GET",
    });
  },
  getDetails(id) {
    return apiRequest(`${USER_LIST_URL}/details/${id}`, {
      method: "GET",
    });
  },
  create(body) {
    return apiRequest(USER_ADD_URL, {
      method: "POST",
      body,
    });
  },
  update(id, body) {
    return apiRequest(`${USER_LIST_URL}/${id}`, {
      method: "PUT",
      body,
    });
  },
  updateStatus(id, status) {
    return apiRequest(`${USER_LIST_URL}/${id}/status`, {
      method: "PATCH",
      body: {
        status,
      },
    });
  },
  remove(id) {
    return apiRequest(`${API_BASE_URL}/api/users/delete/${id}`, {
      method: "DELETE",
    });
  },
  getByEmail(email) {
    const formData = new FormData();
    formData.append("email", email);

    return apiRequest(USER_BY_EMAIL_URL, {
      method: "POST",
      body: formData,
    });
  },
};

export const listingApi = {
  ...createCrudApi("/api/user-listings"),
  uploadProperty(body) {
    return apiRequest(PROPERTY_UPLOAD_URL, {
      method: "POST",
      body,
    });
  },
};

export const societyApi = {
  create(body) {
    return apiRequest(SOCIETY_ADD_URL, {
      method: "POST",
      body,
    });
  },
  async getAll() {
    const data = await apiRequest(SOCIETY_MASTER_URL, {
      method: "GET",
    });

    return normalizeSocietyListResponse(data);
  },
  async getById(id) {
    const data = await apiRequest(`${SOCIETY_MASTER_URL}/${id}`, {
      method: "GET",
    });

    return unwrapResponseData(data);
  },
  update(id, body) {
    return apiRequest(`${SOCIETY_MASTER_URL}/${id}`, {
      method: "PUT",
      body,
    });
  },
  remove(id) {
    return apiRequest(`${API_BASE_URL}/api/society/master/delete/${id}`, {
      method: "DELETE",
    });
  },
  async getSummaryList() {
    const data = await apiRequest(SOCIETY_SUMMARY_LIST_URL, {
      method: "GET",
    });

    return normalizeSocietyListResponse(data);
  },
};

export const bookedUnitApi = {
  create(body) {
    return apiRequest(BOOKED_UNIT_URL, {
      method: "POST",
      body,
    });
  },
  getDetails(id) {
    return apiRequest(`${BOOKED_UNIT_DETAIL_URL}/${id}`, {
      method: "GET",
    });
  },
  getByPropertyId(propertyId) {
    return apiRequest(`${API_BASE_URL}/api/tenant/by-property-id/${propertyId}`, {
      method: "GET",
    });
  },
};

export const authApi = {
  requestOtp({ mobile, email, scope }) {
    const mobileNumber = mobile || email;

    return apiRequest(OTP_SEND_URL, {
      method: "POST",
      body: { mobile: mobileNumber },
    });
  },
  verifyOtp({ mobile, email, otp }) {
    const mobileNumber = mobile || email;

    return apiRequest(OTP_VERIFY_URL, {
      method: "POST",
      body: { mobile: mobileNumber, otp },
    });
  },
};

export { IMAGE_BASE_URL };
